import { USER_UUID_COOKIE_NAME } from "@/configs/constants";
import { useAppSelector } from "@/store/hooks";
import { initializeAlgoliaAnalytics } from "@/utils/algoliaAnalytics";
import Cookies from "js-cookie";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import aa from "search-insights";
import { useDebouncedCallback } from "use-debounce";

export default function useSearchRouter(indexName?: string) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const algoliaAppId = useAppSelector((state) => state.config.algoliaAppId);
  const algoliaSearchApiKey = useAppSelector((state) => state.config.algoliaSearchApiKey);
  // Get sortBy from the URL params
  const sortBy = searchParams?.get("sortBy") || "";

  const pushUrl = useCallback(
    (uri: string) => {
      router.push(uri);
    },
    [router],
  );

  const sendFilterClickEvent = useCallback(
    (modifiedParams: ModifySearchParams[]) => {
      if (!indexName) return;
      // Format each facet to match Algolia's facetFilters format used server-side in
      // src/app/search/queryConversion/convertQuery.ts so the event matches the
      // filters Algolia actually applies to the search.
      const filters = modifiedParams
        .filter((p) => p.facetValue)
        .map(({ facetName, facetValue }) => {
          if (facetName === "category") {
            const level = facetValue.split(" > ").length - 1;
            return `category.lvl${level}:${facetValue}`;
          }
          return `${facetName}:${facetValue}`;
        });
      if (filters.length === 0) return;
      if (!initializeAlgoliaAnalytics(algoliaAppId, algoliaSearchApiKey)) {
        console.warn("Algolia analytics initialization failed, skipping event");
        return;
      }
      try {
        const userToken = Cookies.get(USER_UUID_COOKIE_NAME);
        aa("clickedFilters", {
          eventName: "Filter Clicked",
          index: indexName,
          filters,
          ...(userToken ? { userToken } : {}),
        });
      } catch (error) {
        console.error("Failed to send Algolia clickedFilters event:", error);
      }
    },
    [indexName, algoliaAppId, algoliaSearchApiKey],
  );

  const debouncedModifyFacets = useDebouncedCallback((allFacetGroups: Record<string, Record<string, boolean>>) => {
    const modifiedParams: ModifySearchParams[] = Object.entries(allFacetGroups).flatMap(([facetName, facetValues]) =>
      Object.entries(facetValues).map(([val, isChecked]) => ({
        facetName,
        facetValue: val,
        deleteFacet: !isChecked,
      })),
    );
    modifyFacets(modifiedParams);
  }, 1000);

  const modifyFacets = useCallback(
    (modifiedParams: ModifySearchParams[]) => {
      const query = new URLSearchParams(searchParams?.toString() || "");

      modifiedParams.forEach(({ facetName, facetValue, deleteFacet, removeExistingOfSame }) => {
        const paramKey = `facet.${facetName}`;

        if (removeExistingOfSame) {
          // Get all current values
          const currentValues = query.getAll(paramKey);
          query.delete(paramKey);
        }
        if (deleteFacet) {
          // Get all current values
          const currentValues = query.getAll(paramKey);
          query.delete(paramKey);
          // if our currentValues are contain condensed facetValues, we have to split them out to remove the facet we want to delete from the condensed string
          if (currentValues[0]?.includes("~")) {
            const fullFacetValues = currentValues[0].split("~");
            const filteredFacetValues = fullFacetValues.filter((value) => value !== facetValue);
            filteredFacetValues.forEach((value) => {
              query.append(paramKey, value);
            });
          } else {
            // Remove only the one we want to delete
            currentValues.filter((value) => value !== facetValue).forEach((value) => query.append(paramKey, value));
          }
        } else {
          // Only append if not already present
          const existing = query.getAll(paramKey);
          // if we condensed the facet values into 1, we need to check each of the values that were condensed into 1 param
          if (existing[0]?.includes("~")) {
            const facetExists = containsCondensedFacet(existing[0], facetValue);
            if (!facetExists) {
              query.append(paramKey, facetValue);
            }
          } else {
            if (!existing.includes(facetValue)) {
              query.append(paramKey, facetValue);
            }
          }
        }
      });

      if (!pathname) {
        console.warn("modifyFacets: pathname is null or undefined.");
        return;
      }

      // set all like params to have separated by commas instead of repeating param name (ex facet.manufacturer=x,y instead of facet.manufactur=x&facet.manufacturer=y)
      const uniqueParams = new Set<string>();

      const urlParams = Array.from(query.entries());

      urlParams.forEach(([key, value]) => {
        uniqueParams.add(key);
      });

      Array.from(uniqueParams).forEach((param) => {
        query.set(param, query.getAll(param).join("~"));
      });

      // Remove trailing page number if it exists (e.g., /.../2)
      const cleanedPath = pathname.replace(/\/\d+$/, "");

      sendFilterClickEvent(modifiedParams);

      pushUrl(cleanedPath + "?" + query.toString());
    },
    [pathname, searchParams, pushUrl, sendFilterClickEvent],
  );

  const modifySortBy = useCallback(
    (sortBy: string) => {
      const query = new URLSearchParams(searchParams?.toString() || "");
      query.set("sortBy", sortBy);
      if (!pathname) {
        console.warn("modifySortBy: pathname is null or undefined.");
        return;
      }
      // Remove trailing page number if it exists (e.g., /.../2)
      const cleanedPath = pathname.replace(/\/\d+$/, "");

      pushUrl(cleanedPath + "?" + query.toString());
    },
    [pathname, searchParams, pushUrl],
  );

  const isFacetActive = useCallback(
    (facetName: string, facetValue: string) => {
      let facetActive = false;
      if (searchParams) {
        searchParams.getAll("facet." + facetName).forEach((facet) => {
          if (facet.includes("~")) {
            facetActive = containsCondensedFacet(facet, facetValue);
          } else {
            if (
              searchParams.getAll(facetName).includes(facetValue) ||
              searchParams.getAll("facet." + facetName).includes(facetValue)
            ) {
              facetActive = true;
            }
          }
        });

        return facetActive;
      }
      return false;
    },
    [searchParams],
  );

  const isCustomFacetActive = useCallback(
    (name: string) => {
      return searchParams?.get(name) === "true";
    },
    [searchParams],
  );

  const removeCustomFacet = useCallback(
    (name: string) => {
      if (!pathname) {
        console.warn("removeCustomFacet: pathname is null or undefined.");
        return;
      }

      // Clone the current search params
      const query = new URLSearchParams(searchParams?.toString() || "");
      query.delete(name);
      const cleanedPath = pathname.replace(/\/\d+$/, "");

      pushUrl(cleanedPath + "?" + query.toString());
    },
    [pathname, searchParams, pushUrl],
  );

  const modifyCustomFacet = useCallback(
    (flag: boolean, id: string) => {
      const query = new URLSearchParams(searchParams?.toString() || "");
      if (flag) {
        query.set(id, flag.toString());
      } else {
        query.delete(id);
      }
      if (!pathname) {
        console.warn("modifyFacets: pathname is null or undefined.");
        return;
      }

      const queryParamCount = query.size;
      // If base URL had no query params, add empty query= param to fix App Router issue
      // This ensures the page reloads properly when clearing facets from base URLs (Weekly Specials, Search, Category pages, etc.)
      if (queryParamCount == 0) {
        query.set("query", "");
      }

      // Remove trailing page number if it exists (e.g., /.../2)
      const cleanedPath = pathname.replace(/\/\d+$/, "");

      pushUrl(cleanedPath + "?" + query.toString());
    },
    [pathname, searchParams, pushUrl],
  );
  /**
   * Used as a pass-through for useRouter.push, this will preserve the
   * current facets such as when changing the page number.
   *
   */
  const push = useCallback(
    (uri: string) => {
      if (searchParams) {
        router.push(uri + "?" + searchParams.toString());
      } else {
        router.push(uri);
      }
    },
    [router, searchParams],
  );

  const removeAllRefinements = useCallback(() => {
    if (!pathname) {
      console.warn("removeAllRefinements: pathname is null or undefined.");
      return;
    }

    const query = new URLSearchParams(window.location.search || "");
    const queryParam = query.getAll("query");
    const tag = query.get("tag");

    // Remove all facet.*, facet.attributes.*, and purchasedBefore keys
    Array.from(query.keys()).forEach((key) => {
      if (key.startsWith("facet.") || key.startsWith("facet.attributes.") || key === "purchasedBefore") {
        query.delete(key);
      }
    });

    const newQuery = new URLSearchParams();

    newQuery.set("query", queryParam.length > 0 ? queryParam.toString() : "");

    if (tag && tag.trim() !== "") {
      newQuery.set("tag", tag);
    }

    const cleanedPath = pathname.replace(/\/\d+$/, "");

    pushUrl(`${cleanedPath}?${newQuery.toString()}`);
  }, [pathname, pushUrl]);

  return {
    modifyFacets,
    modifySortBy,
    isFacetActive,
    modifyCustomFacet,
    push,
    sortBy,
    isCustomFacetActive,
    removeCustomFacet,
    debouncedModifyFacets,
    removeAllRefinements,
  };
}

export type ModifySearchParams = {
  facetName: string;
  facetValue: string;
  deleteFacet: boolean;
  removeExistingOfSame?: boolean;
};

function containsCondensedFacet(fullFacet: string, facetValue: string): boolean {
  let facetActive = false;
  const separatedFacetValues: string[] = [];
  const fullFacetValues = fullFacet.split("~");

  fullFacetValues.forEach((value) => {
    separatedFacetValues.push(value);
  });

  separatedFacetValues.forEach((value) => {
    if (value === facetValue) {
      facetActive = true;
    }
  });

  return facetActive;
}
